import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
    Plus, CheckSquare, Calendar, Loader2, Clock, X, User, MapPin, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { tasksAPI, calendarAPI, usersAPI } from '@/lib/api';
import { format, addHours } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '@/lib/auth';

const EVENT_TYPES = [
    { id: 'rdv', label: 'Rendez-vous', color: 'bg-blue-500' },
    { id: 'call', label: 'Appel', color: 'bg-green-500' },
    { id: 'visite', label: 'Visite', color: 'bg-purple-500' },
    { id: 'reunion', label: 'Réunion', color: 'bg-orange-500' },
    { id: 'autre', label: 'Autre', color: 'bg-slate-500' },
];

export function QuickTaskEventCreator({ prospect, onCreated }) {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState('event');
    const [saving, setSaving] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const titleRef = useRef(null);
    
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    
    // Charger les utilisateurs assignables
    useEffect(() => {
        usersAPI.getAll().then(res => setAllUsers(res.data || [])).catch(() => {});
    }, []);
    
    // Filtrer les users selon les règles
    const assignableUsers = allUsers.filter(u => {
        if (u.id === user?.id || u.is_active === false) return false;
        if (isAdmin) return true;
        if (isVP) return u.role === 'admin_full' || u.role === 'admin' || u.vp_id === user?.id;
        // Apporteur
        return u.role === 'admin_full' || u.role === 'admin' || u.id === user?.vp_id || (u.role === 'apporteur' && u.vp_id === user?.vp_id && user?.vp_id);
    });
    
    // Form state pour événement
    const [eventForm, setEventForm] = useState({
        title: '',
        description: '',
        event_type: 'rdv',
        module: 'easy_power',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '10:00',
        location: '',
        assigned_to: '',
        participant_ids: [],
        all_day: false,
        color: '#3b82f6',
        comment: ''
    });
    
    // Form state pour tâche
    const [taskForm, setTaskForm] = useState({
        title: '',
        description: '',
        priority: 'medium',
        due_date: format(new Date(), 'yyyy-MM-dd'),
        module: 'easy_power',
        assigned_to: '',
        assignee_ids: []
    });
    
    // Focus sur le titre à l'ouverture
    useEffect(() => {
        if (isOpen && titleRef.current) {
            setTimeout(() => titleRef.current?.focus(), 100);
        }
    }, [isOpen, mode]);
    
    // Reset forms
    const resetForms = () => {
        const now = new Date();
        const nextHour = addHours(now, 1);
        setEventForm({
            title: '',
            description: '',
            event_type: 'rdv',
            module: 'easy_power',
            start_date: format(now, 'yyyy-MM-dd'),
            start_time: format(nextHour, 'HH:00'),
            end_time: format(addHours(nextHour, 1), 'HH:00'),
            location: '',
            assigned_to: '',
            participant_ids: [],
            all_day: false,
            color: '#3b82f6',
            comment: ''
        });
        setTaskForm({
            title: '',
            description: '',
            priority: 'medium',
            due_date: format(now, 'yyyy-MM-dd'),
            module: 'easy_power',
            assigned_to: '',
            assignee_ids: []
        });
    };
    
    const handleOpen = (openMode) => {
        setMode(openMode);
        resetForms();
        setIsOpen(true);
    };
    
    const handleClose = () => {
        setIsOpen(false);
        resetForms();
    };
    
    const handleSaveEvent = async () => {
        if (!eventForm.title.trim()) {
            toast.error('Le titre est obligatoire');
            return;
        }
        if (!eventForm.start_date) {
            toast.error('La date est obligatoire');
            return;
        }
        
        setSaving(true);
        try {
            const startDateTime = `${eventForm.start_date}T${eventForm.start_time}:00`;
            const endDateTime = `${eventForm.start_date}T${eventForm.end_time}:00`;
            
            // Fusion participants multi + assigned_to legacy
            const participantIds = [...(eventForm.participant_ids || [])];
            const finalAssignedTo = eventForm.assigned_to || (participantIds[0] || null);
            if (finalAssignedTo && !participantIds.includes(finalAssignedTo)) {
                participantIds.unshift(finalAssignedTo);
            }
            
            await calendarAPI.createEvent({
                title: eventForm.title,
                description: eventForm.description + (eventForm.comment ? `\n\n--- Commentaire ---\n${eventForm.comment}` : ''),
                event_type: eventForm.event_type,
                module: eventForm.module,
                start_datetime: startDateTime,
                end_datetime: endDateTime,
                prospect_id: prospect.id,
                location: eventForm.location || null,
                assigned_to: finalAssignedTo,
                participant_ids: participantIds,
                all_day: eventForm.all_day || false,
                color: eventForm.color || '#3b82f6'
            });
            
            toast.success('Événement créé avec succès');
            handleClose();
            if (onCreated) onCreated();
        } catch (err) {
            console.error('Erreur création événement:', err);
            toast.error(err.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };
    
    const handleSaveTask = async () => {
        if (!taskForm.title.trim()) {
            toast.error('Le titre est obligatoire');
            return;
        }
        
        setSaving(true);
        try {
            // Fusion assignee_ids multi + assigned_to legacy
            const assigneeIds = [...(taskForm.assignee_ids || [])];
            if (taskForm.assigned_to && !assigneeIds.includes(taskForm.assigned_to)) {
                assigneeIds.unshift(taskForm.assigned_to);
            }
            await tasksAPI.create({
                title: taskForm.title,
                description: taskForm.description,
                priority: taskForm.priority,
                due_date: taskForm.due_date || null,
                prospect_id: prospect.id,
                module: taskForm.module,
                assigned_to: assigneeIds[0] || null,
                assignee_ids: assigneeIds
            });
            
            toast.success('Tâche créée avec succès');
            handleClose();
            if (onCreated) onCreated();
        } catch (err) {
            console.error('Erreur création tâche:', err);
            toast.error(err.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };
    
    const prospectName = prospect.company_name || `${prospect.first_name} ${prospect.last_name}`;
    const currentEventType = EVENT_TYPES.find(t => t.id === eventForm.event_type) || EVENT_TYPES[0];
    
    return (
        <Popover open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <div className="flex gap-2">
                <PopoverTrigger asChild>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2"
                        onClick={() => handleOpen('event')}
                        data-testid="quick-event-btn"
                    >
                        <Calendar className="w-4 h-4" />
                        Nouveau RDV
                    </Button>
                </PopoverTrigger>
                <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2"
                    onClick={() => handleOpen('task')}
                    data-testid="quick-task-btn"
                >
                    <CheckSquare className="w-4 h-4" />
                    Nouvelle tâche
                </Button>
            </div>
            
            <PopoverContent 
                className="w-[400px] p-0 shadow-xl" 
                align="start"
                sideOffset={8}
            >
                {mode === 'event' ? (
                    /* ========== FORMULAIRE ÉVÉNEMENT (style Google Calendar) ========== */
                    <div className="flex flex-col">
                        {/* Header avec type d'événement */}
                        <div className={`${currentEventType.color} px-4 py-3 flex items-center justify-between`}>
                            <Select 
                                value={eventForm.event_type} 
                                onValueChange={(v) => setEventForm(prev => ({ ...prev, event_type: v }))}
                            >
                                <SelectTrigger className="w-auto border-0 bg-transparent text-white font-medium hover:bg-white/20 h-8 gap-2">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {EVENT_TYPES.map(type => (
                                        <SelectItem key={type.id} value={type.id}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${type.color}`}></div>
                                                {type.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-white hover:bg-white/20 h-8 w-8"
                                onClick={handleClose}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                        
                        {/* Contenu */}
                        <div className="p-4 space-y-4">
                            {/* Titre */}
                            <Input
                                ref={titleRef}
                                value={eventForm.title}
                                onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Ajouter un titre"
                                className="text-lg border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500"
                                data-testid="event-title-input"
                            />
                            
                            {/* Prospect associé (lecture seule) */}
                            <div className="flex items-center gap-3 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md">
                                <User className="w-4 h-4" />
                                <span>Associé à : <strong>{prospectName}</strong></span>
                            </div>
                            
                            {/* Date et heures */}
                            <div className="flex items-center gap-3">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <div className="flex items-center gap-2 flex-1">
                                    <Input
                                        type="date"
                                        value={eventForm.start_date}
                                        onChange={(e) => setEventForm(prev => ({ ...prev, start_date: e.target.value }))}
                                        className="flex-1"
                                        data-testid="event-date-input"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3 ml-7">
                                <Select 
                                    value={eventForm.start_time} 
                                    onValueChange={(v) => setEventForm(prev => ({ ...prev, start_time: v }))}
                                >
                                    <SelectTrigger className="w-24" data-testid="event-start-time">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-48">
                                        {Array.from({ length: 24 }, (_, h) => [
                                            `${h.toString().padStart(2, '0')}:00`,
                                            `${h.toString().padStart(2, '0')}:30`
                                        ]).flat().map(time => (
                                            <SelectItem key={time} value={time}>{time}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-slate-400">-</span>
                                <Select 
                                    value={eventForm.end_time} 
                                    onValueChange={(v) => setEventForm(prev => ({ ...prev, end_time: v }))}
                                >
                                    <SelectTrigger className="w-24" data-testid="event-end-time">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-48">
                                        {Array.from({ length: 24 }, (_, h) => [
                                            `${h.toString().padStart(2, '0')}:00`,
                                            `${h.toString().padStart(2, '0')}:30`
                                        ]).flat().map(time => (
                                            <SelectItem key={time} value={time}>{time}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Lieu (optionnel) */}
                            <div className="flex items-center gap-3">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <Input
                                    value={eventForm.location}
                                    onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                                    placeholder="Ajouter un lieu"
                                    className="flex-1"
                                />
                            </div>

                            {/* Module / Mur du dossier (obligatoire, comme les tâches) */}
                            <div className="space-y-2">
                                <Label className="text-xs text-slate-500">Mur du dossier concerné</Label>
                                <Select
                                    value={eventForm.module}
                                    onValueChange={(v) => setEventForm(prev => ({ ...prev, module: v }))}
                                >
                                    <SelectTrigger data-testid="event-module">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="easy_power">Easy Power</SelectItem>
                                        <SelectItem value="chantier">Chantier</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-slate-400">L'événement apparaîtra sur le mur de ce module.</p>
                            </div>
                            
                            {/* Assigner des participants - multi */}
                            <div className="flex items-start gap-3">
                                <UserPlus className="w-4 h-4 text-slate-400 mt-2" />
                                <div className="flex-1 space-y-1">
                                    <div className="text-xs text-slate-500">Participants <span className="text-slate-400">(plusieurs possible)</span></div>
                                    <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto bg-white" data-testid="quick-event-participants">
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                checked={eventForm.participant_ids.includes(user?.id)}
                                                onChange={(e) => {
                                                    setEventForm(prev => ({
                                                        ...prev,
                                                        participant_ids: e.target.checked
                                                            ? Array.from(new Set([...(prev.participant_ids || []), user.id]))
                                                            : (prev.participant_ids || []).filter(id => id !== user?.id)
                                                    }));
                                                }}
                                            />
                                            <span className="text-sm">Moi-même</span>
                                        </label>
                                        {assignableUsers.map(u => (
                                            <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                    checked={eventForm.participant_ids.includes(u.id)}
                                                    onChange={(e) => {
                                                        setEventForm(prev => ({
                                                            ...prev,
                                                            participant_ids: e.target.checked
                                                                ? Array.from(new Set([...(prev.participant_ids || []), u.id]))
                                                                : (prev.participant_ids || []).filter(id => id !== u.id)
                                                        }));
                                                    }}
                                                />
                                                <span className="text-sm">{u.first_name} {u.last_name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Journée entière + Couleur */}
                            <div className="flex items-center justify-between gap-3">
                                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={eventForm.all_day} 
                                        onChange={e => setEventForm(prev => ({...prev, all_day: e.target.checked}))}
                                        className="w-4 h-4 rounded"
                                    />
                                    Journée entière
                                </label>
                                <div className="flex gap-1.5">
                                    {['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'].map(c => (
                                        <button
                                            key={c}
                                            type="button"
                                            className={`w-5 h-5 rounded-full border-2 transition-transform ${eventForm.color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                                            style={{backgroundColor: c}}
                                            onClick={() => setEventForm(prev => ({...prev, color: c}))}
                                        />
                                    ))}
                                </div>
                            </div>
                            
                            {/* Description */}
                            <Textarea
                                value={eventForm.description}
                                onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Description de l'événement..."
                                rows={2}
                                className="resize-none"
                            />
                            
                            {/* Commentaire */}
                            <Textarea
                                value={eventForm.comment}
                                onChange={(e) => setEventForm(prev => ({ ...prev, comment: e.target.value }))}
                                placeholder="Commentaire ou détails supplémentaires..."
                                rows={2}
                                className="resize-none bg-amber-50/50 border-amber-200"
                            />
                        </div>
                        
                        {/* Footer */}
                        <div className="px-4 py-3 border-t flex justify-end gap-2">
                            <Button variant="ghost" onClick={handleClose} disabled={saving}>
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleSaveEvent} 
                                disabled={saving}
                                className={currentEventType.color}
                                data-testid="save-event-btn"
                            >
                                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Enregistrer
                            </Button>
                        </div>
                    </div>
                ) : (
                    /* ========== FORMULAIRE TÂCHE ========== */
                    <div className="flex flex-col">
                        {/* Header */}
                        <div className="bg-amber-500 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white font-medium">
                                <CheckSquare className="w-4 h-4" />
                                Nouvelle tâche
                            </div>
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-white hover:bg-white/20 h-8 w-8"
                                onClick={handleClose}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                        
                        {/* Contenu */}
                        <div className="p-4 space-y-4">
                            {/* Titre */}
                            <Input
                                ref={titleRef}
                                value={taskForm.title}
                                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Titre de la tâche"
                                className="text-lg border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-amber-500"
                                data-testid="task-title-input"
                            />
                            
                            {/* Prospect associé */}
                            <div className="flex items-center gap-3 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md">
                                <User className="w-4 h-4" />
                                <span>Associé à : <strong>{prospectName}</strong></span>
                            </div>
                            
                            {/* Date et priorité */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-slate-500">Date limite</Label>
                                    <Input
                                        type="date"
                                        value={taskForm.due_date}
                                        onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                                        data-testid="task-due-date"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-slate-500">Priorité</Label>
                                    <Select 
                                        value={taskForm.priority} 
                                        onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                                    >
                                        <SelectTrigger data-testid="task-priority">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Basse</SelectItem>
                                            <SelectItem value="medium">Moyenne</SelectItem>
                                            <SelectItem value="high">Haute</SelectItem>
                                            <SelectItem value="urgent">Urgente</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            {/* Module / Mur du dossier */}
                            <div className="space-y-2">
                                <Label className="text-xs text-slate-500">Mur du dossier concerné</Label>
                                <Select 
                                    value={taskForm.module} 
                                    onValueChange={(v) => setTaskForm(prev => ({ ...prev, module: v }))}
                                >
                                    <SelectTrigger data-testid="task-module">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="easy_power">Easy Power</SelectItem>
                                        <SelectItem value="chantier">Chantier</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-slate-400">La tâche apparaîtra sur le mur de ce module.</p>
                            </div>
                            
                            {/* Assigner la tâche - multi */}
                            <div className="flex items-start gap-3">
                                <UserPlus className="w-4 h-4 text-slate-400 mt-2" />
                                <div className="flex-1 space-y-1">
                                    <div className="text-xs text-slate-500">Assignés <span className="text-slate-400">(plusieurs possible)</span></div>
                                    <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto bg-white" data-testid="quick-task-assignees">
                                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                checked={taskForm.assignee_ids.includes(user?.id)}
                                                onChange={(e) => {
                                                    setTaskForm(prev => ({
                                                        ...prev,
                                                        assignee_ids: e.target.checked
                                                            ? Array.from(new Set([...(prev.assignee_ids || []), user.id]))
                                                            : (prev.assignee_ids || []).filter(id => id !== user?.id)
                                                    }));
                                                }}
                                            />
                                            <span className="text-sm">Moi-même</span>
                                        </label>
                                        {assignableUsers.map(u => (
                                            <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                    checked={taskForm.assignee_ids.includes(u.id)}
                                                    onChange={(e) => {
                                                        setTaskForm(prev => ({
                                                            ...prev,
                                                            assignee_ids: e.target.checked
                                                                ? Array.from(new Set([...(prev.assignee_ids || []), u.id]))
                                                                : (prev.assignee_ids || []).filter(id => id !== u.id)
                                                        }));
                                                    }}
                                                />
                                                <span className="text-sm">{u.first_name} {u.last_name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Description */}
                            <Textarea
                                value={taskForm.description}
                                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Description ou détails..."
                                rows={2}
                                className="resize-none"
                            />
                        </div>
                        
                        {/* Footer */}
                        <div className="px-4 py-3 border-t flex justify-end gap-2">
                            <Button variant="ghost" onClick={handleClose} disabled={saving}>
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleSaveTask} 
                                disabled={saving}
                                className="bg-amber-500 hover:bg-amber-600"
                                data-testid="save-task-btn"
                            >
                                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Créer la tâche
                            </Button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
